<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import type { Services } from "@services"
import type { BuildingVisibleToEntry } from "@types"
import type { ItemBuildingData } from "@core/components/item/ItemBuilding.vue"

defineOptions({
    name: "HomeBuildingDialogs",
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const homeWorkspace = useHomeWorkspaceStore()
const buildingStore = useBuildingStore()
const { modal } = storeToRefs(homeWorkspace)
const { $deleteBuilding } = inject<Services>("services")!

// ================================
// STATE
// ================================
/**
 * Copie mutable du récap bâtiment pour rafraîchir les avatars après sauvegarde des accès.
 */
const buildingAccessItemPatched = ref<ItemBuildingData | null>(null)
/** Nom saisi pour le renommage d’un bâtiment. */
const renameName = ref("")
/** Verrou UI pendant les appels API. */
const loading = ref(false)

// ================================
// COMPUTED
// ================================
/** Id bâtiment pour le dialogue de partage (liste d’accès). */
const getBuildingAccessBuildingId = computed(
    () => (modal.value.meta.buildingId as string) ?? ""
)

// ================================
// WATCHERS
// ================================
watch(
    () => modal.value,
    (m) => {
        if (m.kind === "buildingRename") {
            renameName.value = (m.meta.name as string) ?? ""
        }
        if (m.kind === "buildingAccess") {
            const id = (m.meta.buildingId as string) ?? ""
            const raw = m.meta.itemBuilding as ItemBuildingData | undefined
            buildingAccessItemPatched.value = raw
                ? { ...raw }
                : { _id: id, name: id || "-" }
        } else {
            buildingAccessItemPatched.value = null
        }
    },
    { deep: true, immediate: true }
)

// ================================
// INTERACTIONS
// ================================
/**
 * Ferme le dialogue courant.
 */
function onClose(): void {
    buildingAccessItemPatched.value = null
    homeWorkspace.closeModal()
}

/**
 * Après mise à jour des accès : store (arbre + enfants) et en-tête du dialogue.
 */
function onBuildingAccessUpdated(payload: {
    buildingId: string
    visibleTo: BuildingVisibleToEntry[]
}): void {
    homeWorkspace.applyBuildingAccessUpdate(
        payload.buildingId,
        payload.visibleTo
    )
    const row = buildingAccessItemPatched.value
    if (row && row._id === payload.buildingId) {
        buildingAccessItemPatched.value = {
            ...row,
            visible_to: payload.visibleTo,
        }
    }
}

/**
 * Enregistre le nouveau nom du bâtiment et met à jour le store.
 */
async function onConfirmRename(): Promise<void> {
    const buildingId = modal.value.meta.buildingId as string
    const nextName = renameName.value.trim()
    if (!buildingId || !nextName) return
    loading.value = true
    try {
        await buildingStore.updateBuildingById(buildingId, { name: nextName })
        homeWorkspace.closeModal()
        homeWorkspace.applyBuildingRename(buildingId, nextName)
    } finally {
        loading.value = false
    }
}

/**
 * Supprime le bâtiment puis met à jour les arbres en cache (sans refetch liste).
 */
async function onConfirmRemove(): Promise<void> {
    const buildingId = modal.value.meta.buildingId as string
    if (!buildingId) return
    loading.value = true
    try {
        await $deleteBuilding(buildingId)
        const routeBuildingParam = route.params._id_building
        const isOnDeletedBuildingRoute =
            typeof routeBuildingParam === "string" &&
            routeBuildingParam === buildingId
        homeWorkspace.closeModal()
        homeWorkspace.applyBuildingDeleted(buildingId)
        if (isOnDeletedBuildingRoute) {
            buildingStore.$reset()
            await router.push({ name: "Home", query: {} })
        }
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div class="home-building-dialogs contents">
        <!-- ================================ -->
        <!-- DIALOGUE — RENOMMER -->
        <!-- ================================ -->
        <BaseDialog
            :visible="modal.kind === 'buildingRename'"
            modal
            header="Renommer le bâtiment"
            class="w-[min(100%,24rem)]"
            @update:visible="(v: boolean) => !v && onClose()"
        >
            <div class="flex flex-col gap-3 py-2">
                <label
                    class="text-sm text-surface-600"
                    for="dashboard-rename-input"
                    >Nom</label
                >
                <BaseInputText
                    id="dashboard-rename-input"
                    v-model="renameName"
                    class="w-full"
                    @keyup.enter="onConfirmRename"
                />
            </div>
            <template #footer>
                <BaseButton
                    label="Annuler"
                    severity="secondary"
                    @click="onClose"
                />
                <BaseButton
                    label="Enregistrer"
                    :loading="loading"
                    @click="onConfirmRename"
                />
            </template>
        </BaseDialog>

        <!-- ================================ -->
        <!-- DIALOGUE — SUPPRIMER -->
        <!-- ================================ -->
        <BaseDialog
            :visible="modal.kind === 'buildingRemove'"
            modal
            header="Supprimer le bâtiment"
            class="w-[min(100%,24rem)]"
            @update:visible="(v: boolean) => !v && onClose()"
        >
            <p class="text-sm text-surface-700">
                Confirmer la suppression de ce bâtiment ?
            </p>
            <template #footer>
                <BaseButton
                    label="Annuler"
                    severity="secondary"
                    @click="onClose"
                />
                <BaseButton
                    label="Supprimer"
                    severity="danger"
                    :loading="loading"
                    @click="onConfirmRemove"
                />
            </template>
        </BaseDialog>

        <!-- ================================ -->
        <!-- DIALOGUE — PARTAGE -->
        <!-- ================================ -->
        <BaseDialog
            :visible="modal.kind === 'buildingAccess'"
            modal
            header="Partage d'accès"
            class="w-[min(100%,50rem)] max-h-[90vh]"
            @update:visible="(v: boolean) => !v && onClose()"
        >
            <div class="flex flex-col gap-4 overflow-y-auto py-1">
                <ItemBuilding
                    v-if="buildingAccessItemPatched"
                    :building="buildingAccessItemPatched"
                />
                <p v-else class="text-sm text-surface-600">
                    Bâtiment :
                    <span class="font-medium">{{
                        getBuildingAccessBuildingId
                    }}</span>
                </p>
                <p class="text-sm text-surface-600">
                    Cochez les collaborateurs autorisés à accéder à ce bâtiment.
                </p>
                <AccessBuildingListUser
                    v-if="getBuildingAccessBuildingId"
                    :building-id="getBuildingAccessBuildingId"
                    class="max-w-none"
                    list-height="min(380px, 45vh)"
                    @access-updated="onBuildingAccessUpdated"
                />
            </div>
            <template #footer>
                <BaseButton
                    label="Fermer"
                    severity="secondary"
                    @click="onClose"
                />
            </template>
        </BaseDialog>

        <!-- ================================ -->
        <!-- DIALOGUE — ÉTIQUETTES BÂTIMENT -->
        <!-- ================================ -->
        <HomeBuildingAddTagsDialog :visible="modal.kind === 'buildingTags'" />

        <!-- ================================ -->
        <!-- DIALOGUE — DÉPLACER -->
        <!-- ================================ -->
        <HomeMoveToFolderDialog
            :visible="modal.kind === 'buildingMove'"
            mode="building"
            :building-ids="(modal.meta.buildingIds as string[]) ?? []"
            :source-folder-id="
                (modal.meta.folderId as string | undefined) || null
            "
            @update:visible="(v: boolean) => !v && onClose()"
        />
    </div>
</template>
